import React, { useState, useEffect } from 'react';
import { Container, Typography, Button, Card, CardContent, FormControl, RadioGroup, FormControlLabel, Radio, Box, CircularProgress, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const categories = ['Communication', 'Routine', 'Safety', 'Empathy', 'Engagement'];

const caregiverTips = {
  en: [
    'Use clear, simple language and maintain eye contact.',
    'Establish a daily routine to provide structure and reduce confusion.',
    'Ensure a safe environment by removing hazards and using labels.',
    'Be patient, listen actively, and validate their feelings.',
    'Engage in familiar and meaningful activities together.'
  ],
  es: [
    'Utilice un lenguaje claro y simple y mantenga el contacto visual.',
    'Establezca una rutina diaria para proporcionar estructura y reducir la confusión.',
    'Garantice un entorno seguro eliminando peligros y usando etiquetas.',
    'Sea paciente, escuche activamente y valide sus sentimientos.',
    'Participe en actividades significativas y familiares juntos.'
  ]
};

export default function CaregiverQuizApp() {
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [language, setLanguage] = useState('en');

  useEffect(() => {
    fetch('/data/questions.json')
      .then(res => res.json())
      .then(data => {
        setQuestions(data);
        setLoading(false);
      });
  }, []);

  const handleChange = (index, value) => {
    setAnswers({ ...answers, [index]: value });
  };

  const handleSubmit = () => {
    setSubmitted(true);
  };

  const handleLanguageChange = (_, newLang) => {
    if (newLang) setLanguage(newLang);
  };

  const results = questions.map((q, index) => {
    const userAnswer = answers[index];
    return {
      ...q,
      userAnswer,
      isCorrect: userAnswer === q.correct
    };
  });

  const categoryResults = categories.map(cat => {
    const catQs = results.filter(r => r.category === cat);
    const correct = catQs.filter(q => q.isCorrect).length;
    return { category: cat, correct, total: catQs.length };
  });

  return (
    <Container maxWidth="md">
      <Box display="flex" justifyContent="space-between" alignItems="center" my={2}>
        <Typography variant="h4">
          {language === 'en'
            ? 'Caregiver Tips & Quiz for Supporting Those with Mild Cognitive Impairment'
            : 'Consejos y prueba para cuidadores que apoyan a personas con deterioro cognitivo leve'}
        </Typography>
        <ToggleButtonGroup
          value={language}
          exclusive
          onChange={handleLanguageChange}
          aria-label="Language selection"
        >
          <ToggleButton value="en">English</ToggleButton>
          <ToggleButton value="es">Español</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {caregiverTips[language].map((tip, i) => (
        <Typography key={i} paragraph>{tip}</Typography>
      ))}

      {loading ? (
        <Box textAlign="center" my={4}>
          <CircularProgress />
        </Box>
      ) : !submitted ? (
        <>
          {questions.map((q, i) => (
            <Card key={i} style={{ marginTop: 20 }}>
              <CardContent>
                <Typography variant="h6">{`Q${i + 1}. ${q.question}`}</Typography>
                <FormControl component="fieldset">
                  <RadioGroup
                    value={answers[i] || ''}
                    onChange={(e) => handleChange(i, e.target.value)}
                  >
                    {Object.entries(q.options).map(([k, v]) => (
                      <FormControlLabel key={k} value={k} control={<Radio />} label={`${k}. ${v}`} />
                    ))}
                  </RadioGroup>
                </FormControl>
              </CardContent>
            </Card>
          ))}
          <Box my={4} textAlign="center">
            <Button variant="contained" onClick={handleSubmit}>
              {language === 'en' ? 'Submit Quiz' : 'Enviar prueba'}
            </Button>
          </Box>
        </>
      ) : (
        <Box>
          <Typography variant="h5" gutterBottom>
            {language === 'en' ? 'Results' : 'Resultados'}
          </Typography>
          <Typography variant="body1">
            {language === 'en'
              ? `You scored ${results.filter(r => r.isCorrect).length} out of ${questions.length}`
              : `Obtuviste ${results.filter(r => r.isCorrect).length} de ${questions.length}`}
          </Typography>

          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={categoryResults}>
              <XAxis dataKey="category" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Bar dataKey="correct" name={language === 'en' ? 'Correct Answers' : 'Respuestas correctas'} />
            </BarChart>
          </ResponsiveContainer>

          {results.map((res, i) => (
            <Card key={i} style={{ marginTop: 20, backgroundColor: res.isCorrect ? '#e8f5e9' : '#ffebee' }}>
              <CardContent>
                <Typography variant="subtitle1">Q{i + 1}: {res.question}</Typography>
                <Typography variant="body2">
                  {language === 'en' ? 'Your Answer' : 'Tu respuesta'}: {res.userAnswer || (language === 'en' ? 'No answer' : 'Sin respuesta')}
                </Typography>
                <Typography variant="body2">
                  {language === 'en' ? 'Correct Answer' : 'Respuesta correcta'}: {res.correct}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Container>
  );
}

